import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Гейт-экран: если открыли мобильное приложение с большого экрана
 * (десктоп ≥ 1100px), показываем сообщение «Откройте на телефоне» вместо
 * приложения. UX тут заточен под одно-рукий тап-режим — на ноутбуке всё
 * выглядит «вытянутой колбасой» и неудобно.
 *
 * Кнопка «Всё равно продолжить» сохраняет согласие в sessionStorage
 * для тех кто хочет посмотреть с ноутбука (демо, отладка).
 */

const BREAKPOINT_PX = 1100;
const STORAGE_KEY = 'jb:field-ignore-device-gate';

export function DeviceGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [isWide, setIsWide] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth >= BREAKPOINT_PX,
  );
  const [overridden, setOverridden] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    const onResize = () => setIsWide(window.innerWidth >= BREAKPOINT_PX);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!isWide || overridden) {
    return <>{children}</>;
  }

  const continueAnyway = () => {
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
    setOverridden(true);
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert(t('deviceGate.copied'));
    } catch {
      alert(t('deviceGate.copyFailed'));
    }
  };

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'grid', placeItems: 'center',
      padding: '20px 16px',
      background: 'var(--bg-soft)',
    }}>
      <div className="jb-card" style={{
        maxWidth: 480, width: '100%',
        padding: 'clamp(24px, 6vw, 32px)',
        textAlign: 'center',
      }}>
        {/* Иконка телефона */}
        <div style={{
          width: 80, height: 80,
          margin: '0 auto 18px',
          borderRadius: 20,
          background: 'var(--surface-alt)',
          display: 'grid', placeItems: 'center',
          color: 'var(--accent)',
        }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2.5" />
            <line x1="12" y1="18" x2="12.01" y2="18" />
          </svg>
        </div>

        <div style={{
          fontFamily: 'Newsreader, serif', fontStyle: 'italic',
          fontSize: 26, lineHeight: 1.2, color: 'var(--text)',
          marginBottom: 10,
        }}>
          {t('deviceGate.title')}
        </div>
        <div style={{
          fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.55,
          marginBottom: 22,
        }}>
          {t('deviceGate.descMobile')}
          <br /><br />
          {t('deviceGate.descDesktop')}
        </div>

        <button
          type="button"
          onClick={copyUrl}
          className="jb-btn jb-btn--lg"
          style={{
            width: '100%', padding: '12px 16px',
            fontSize: 14, fontWeight: 600,
            marginBottom: 10,
          }}
        >
          {t('deviceGate.copyLink')}
        </button>

        <button
          type="button"
          onClick={continueAnyway}
          style={{
            width: '100%', padding: '10px',
            background: 'transparent', border: 'none',
            fontSize: 12, color: 'var(--text-muted)',
            cursor: 'pointer', fontFamily: 'inherit',
            textDecoration: 'underline',
            textUnderlineOffset: 3,
          }}
        >
          {t('deviceGate.continueAnyway')}
        </button>

        <div style={{
          marginTop: 18, paddingTop: 14,
          borderTop: '1px solid var(--hairline)',
          fontSize: 11, color: 'var(--text-dim)',
        }}>
          {t('deviceGate.footer')}
        </div>
      </div>
    </div>
  );
}
